import { supabase } from '../lib/supabase';

export const authService = {
  /**
   * Sign up with email and password
   * Profile is automatically created by database trigger
   */
  async signUp(email, password, fullName = '', avatarUrl = '') {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            employee_name: fullName,
            avatar_url: avatarUrl,
            role: 'user'
          }
        }
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Sign in with email and password
   */
  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Sign in with Google OAuth
   */
  async signInWithGoogle() {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location?.origin}/auth/callback`
        }
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Sign in with GitHub OAuth
   */
  async signInWithGithub() {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location?.origin}/auth/callback`
        }
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Sign in with Azure OAuth
   */
  async signInWithAzure() {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'azure',
        options: {
          redirectTo: `${window.location?.origin}/auth/callback`,
          scopes: 'email'
        }
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Sign out current user
   */
  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  /**
   * Get current session
   */
  async getSession() {
    try {
      const { data, error } = await supabase?.auth?.getSession();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Get current user
   */
  async getUser() {
    try {
      const { data, error } = await supabase?.auth?.getUser();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Reset password request
   */
  async resetPassword(email) {
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/auth/reset-password`
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Update password
   */
  async updatePassword(newPassword) {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Get user profile from public.user_profiles
   * This is synced from auth.users via database trigger
   */
  async getProfile() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError) throw authError;
      if (!user) return { data: null, error: null };

      const { data: profile, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', user?.id)
        ?.single();
      
      if (error && error?.code !== 'PGRST116') throw error; // PGRST116 = no rows
      return { data: profile, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Update user profile in public.user_profiles
   */
  async updateProfile(updates) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', user?.id)
        ?.select()
        ?.single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Get user settings from public.user_settings
   */
  async getSettings() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError) throw authError;
      if (!user) return { data: null, error: null };

      const { data: settings, error } = await supabase
        ?.from('user_settings')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.single();
      
      if (error && error?.code !== 'PGRST116') throw error;
      return { data: settings, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Update or create user settings
   */
  async saveSettings(settings) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('user_settings')
        ?.upsert({
          user_id: user?.id,
          ...settings,
          updated_at: new Date()?.toISOString()
        }, {
          onConflict: 'user_id'
        })
        ?.select()
        ?.single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Get combined user data (auth + profile + settings)
   */
  async getFullUserData() {
    try {
      const [authResult, profileResult, settingsResult] = await Promise.all([
        this?.getUser(),
        this?.getProfile(),
        this?.getSettings()
      ]);

      if (authResult?.error) throw authResult?.error;

      return {
        data: {
          auth: authResult?.data?.user,
          profile: profileResult?.data,
          settings: settingsResult?.data
        },
        error: null
      };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// Provide default export for compatibility with services index
export default authService;